import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RoleChips } from "@/components/RoleChips";
import { Clock, Plus, X, ChevronDown } from "lucide-react";

export const shiftText = (s) => s.start_time;

function StaffRow({ r, checked, onToggle, testid }) {
  return (
    <label className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-opacity duration-200 ${checked ? "" : "opacity-60"}`} data-testid={testid}>
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`${testid}-checkbox`} />
      <span className="font-medium flex-1 min-w-0 truncate">{r.staff_name}</span>
      <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[45%]">{r.role}</span>
    </label>
  );
}

function ShiftCard({ shift, selected, staffIds, recipients, onToggleShift, onToggleStaff, onRemove }) {
  const [showOthers, setShowOthers] = useState(false);
  const roles = shift.roles || [];
  const matching = recipients.filter((r) => roles.includes(r.role));
  const others = recipients.filter((r) => !roles.includes(r.role));
  const shownOthers = others.filter((r) => showOthers || staffIds.includes(r.id));
  const row = (r) => (
    <StaffRow key={r.id} r={r} checked={staffIds.includes(r.id)} onToggle={() => onToggleStaff(shift.key, r.id)} testid={`assign-${shift.key}-${r.id}`} />
  );

  return (
    <div className={`rounded-md border ${shift.custom ? "border-dashed border-primary/60" : "border-border"}`} data-testid={`shift-card-${shift.key}`}>
      <div className="flex items-start gap-3 px-3 py-2.5">
        <Checkbox checked={selected} onCheckedChange={() => onToggleShift(shift.key)} data-testid={`shift-checkbox-${shift.key}`} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" />{shiftText(shift)}{shift.custom && <span className="text-xs text-muted-foreground">one-off</span>}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {roles.length ? roles.map((r) => <Badge key={r} variant="secondary" className="rounded-full text-[10px] font-medium">{r}</Badge>)
              : <span className="text-xs text-muted-foreground">No roles set, so tick people below</span>}
          </div>
        </div>
        {selected && <Badge className="rounded-full bg-primary shrink-0" data-testid={`shift-count-${shift.key}`}>{staffIds.length}</Badge>}
        {shift.custom && (
          <button type="button" onClick={() => onRemove(shift.key)} data-testid={`remove-custom-shift-${shift.key}`} className="text-muted-foreground hover:text-destructive transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {selected && (
        <div className="border-t border-border divide-y divide-border max-h-72 overflow-auto">
          {matching.map(row)}
          {matching.length === 0 && roles.length > 0 && <p className="text-xs text-muted-foreground px-3 py-2">No available staff with these roles.</p>}
          {shownOthers.map(row)}
          {others.length > 0 && (
            <button type="button" onClick={() => setShowOthers(!showOthers)} data-testid={`toggle-others-${shift.key}`}
              className="w-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-primary hover:bg-muted/40">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showOthers ? "rotate-180" : ""}`} />
              {showOthers ? "Hide other staff" : `Add other staff (${others.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function OneOffForm({ roleNames, onAdd }) {
  const [time, setTime] = useState("");
  const [roles, setRoles] = useState([]);
  const add = () => { onAdd({ start_time: time, roles }); setTime(""); setRoles([]); };
  return (
    <div className="rounded-md border border-dashed border-border p-3 space-y-2" data-testid="one-off-form">
      <p className="text-xs font-medium">Add a one-off shift (last-minute call-in)</p>
      <div className="flex gap-2">
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="custom-shift-time-input" className="flex-1 min-w-0" />
        <Button type="button" variant="outline" size="icon" onClick={add} disabled={!time} data-testid="add-custom-shift-button" className="shrink-0"><Plus className="h-4 w-4" /></Button>
      </div>
      <RoleChips roles={roleNames} value={roles} onChange={setRoles} testid="custom-shift-roles" />
    </div>
  );
}

export function ShiftAssignments({ shifts, selected, assign, recipients, roleNames, onToggleShift, onToggleStaff, onAdd, onRemove }) {
  return (
    <div className="space-y-2" data-testid="shift-assignments">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Shifts & who's working</p>
      {shifts.length === 0 && <p className="text-xs text-muted-foreground" data-testid="no-scheduled-shifts">No shifts scheduled for this day. Add a one-off shift below.</p>}
      {shifts.map((s) => (
        <ShiftCard key={s.key} shift={s} selected={selected.includes(s.key)} staffIds={assign[s.key] || []} recipients={recipients}
          onToggleShift={onToggleShift} onToggleStaff={onToggleStaff} onRemove={onRemove} />
      ))}
      <OneOffForm roleNames={roleNames} onAdd={onAdd} />
    </div>
  );
}
